import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, FileText, Calendar, CheckCircle, Shield, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!phone || !email) {
      toast.error('Please enter phone and email');
      return;
    }

    setLoading(true);
    try {
      const res = await loadRazorpayScript();
      if (!res) {
        toast.error('Razorpay SDK failed to load');
        return;
      }

      const orderResponse = await axios.post(`${API}/payment/create-order`, { phone, email });
      const { order_id, amount, currency, key_id } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'School Admission',
        description: 'Application Form Fee',
        order_id: order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              phone,
              email
            });

            if (verifyResponse.data.success) {
              toast.success('Payment successful! Credentials sent to your email and phone.');
              setShowPaymentModal(false);
              setShowLoginModal(true);
            }
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          email: email,
          contact: phone
        },
        theme: {
          color: '#002147'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!loginEmail) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { email: loginEmail });
      toast.success('OTP sent to your email');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error('Please enter OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email: loginEmail, otp });
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        navigate('/parent-dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Hero Section */}
      <div className="relative" style={{ background: 'linear-gradient(135deg, #002147 0%, #003566 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 
                className="text-5xl lg:text-6xl font-bold text-white mb-6" 
                style={{ fontFamily: 'Merriweather, serif', lineHeight: '1.2' }}
                data-testid="hero-heading"
              >
                Welcome to Our School Admission Portal
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Start your child's educational journey with us. Simple, secure, and efficient admission process.
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={() => setShowPaymentModal(true)} 
                  className="px-8 py-4 bg-white text-[#002147] rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl"
                  data-testid="apply-now-btn"
                >
                  Apply Now - ₹200
                </button>
                <button 
                  onClick={() => setShowLoginModal(true)} 
                  className="px-8 py-4 bg-[#D4AF37] text-[#002147] rounded-lg font-semibold text-lg hover:bg-[#C19E2F] transition-all duration-200"
                  data-testid="parent-login-btn"
                >
                  Parent Login
                </button>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1666290439931-42f8f4c275b0?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="School Building" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>
          Why Choose Us?
        </h2>
        <p className="text-center text-gray-600 mb-12 text-lg">Experience a seamless admission process</p>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="card text-center p-8">
            <div className="flex justify-center mb-4">
              <FileText size={48} color="#002147" />
            </div>
            <h3 className="text-xl font-semibold mb-3" style={{ color: '#002147' }}>Easy Application</h3>
            <p className="text-gray-600">Simple online form with step-by-step guidance</p>
          </div>

          <div className="card text-center p-8">
            <div className="flex justify-center mb-4">
              <Calendar size={48} color="#002147" />
            </div>
            <h3 className="text-xl font-semibold mb-3" style={{ color: '#002147' }}>Interview Scheduling</h3>
            <p className="text-gray-600">Convenient time slots between 10 AM - 2 PM</p>
          </div>

          <div className="card text-center p-8">
            <div className="flex justify-center mb-4">
              <CheckCircle size={48} color="#002147" />
            </div>
            <h3 className="text-xl font-semibold mb-3" style={{ color: '#002147' }}>Real-time Status</h3>
            <p className="text-gray-600">Track your application status instantly</p>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div style={{ background: '#F3F4F6' }} className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>
            Admission Process
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#002147] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h4 className="font-semibold mb-2" style={{ color: '#002147' }}>Pay Application Fee</h4>
              <p className="text-sm text-gray-600">₹200 secure payment</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#002147] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h4 className="font-semibold mb-2" style={{ color: '#002147' }}>Receive Credentials</h4>
              <p className="text-sm text-gray-600">Login details via SMS & Email</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#002147] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h4 className="font-semibold mb-2" style={{ color: '#002147' }}>Fill Application</h4>
              <p className="text-sm text-gray-600">Complete form with documents</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#002147] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h4 className="font-semibold mb-2" style={{ color: '#002147' }}>Track Status</h4>
              <p className="text-sm text-gray-600">Monitor application progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Link */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-600">Are you an administrator? <button onClick={() => navigate('/admin-login')} className="text-[#002147] font-semibold hover:underline">Login here</button></p>
        </div>
      </div>

      {/* Footer with Policy Links */}
      <footer className="bg-[#002147] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>School Admission Portal</h3>
              <p className="text-gray-300 text-sm">
                Simplifying the admission process for parents and schools. Secure, efficient, and transparent.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/terms-and-conditions')} className="text-gray-300 hover:text-white transition">Terms and Conditions</button></li>
                <li><button onClick={() => navigate('/privacy-policy')} className="text-gray-300 hover:text-white transition">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/shipping-policy')} className="text-gray-300 hover:text-white transition">Application Processing Policy</button></li>
                <li><button onClick={() => navigate('/cancellation-refunds')} className="text-gray-300 hover:text-white transition">Cancellation & Refunds</button></li>
                <li><button onClick={() => navigate('/contact-us')} className="text-gray-300 hover:text-white transition">Contact Us</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-gray-300 flex items-start gap-2">
                  <span>📍</span>
                  <span>Bismillah Educational Trust, 12/5, C Cross Rd, Bismillahnagar, S.G. Palya, Bengaluru, Karnataka 560029</span>
                </li>
                <li className="text-gray-300 flex items-center gap-2">
                  <span>📞</span>
                  <a href="tel:+919663916334" className="hover:text-white transition">+91 9663916334</a>
                </li>
                <li className="text-gray-300 flex items-center gap-2">
                  <span>✉️</span>
                  <a href="mailto:info@betinstitutions.org" className="hover:text-white transition">info@betinstitutions.org</a>
                </li>
                <li className="text-gray-300 flex items-center gap-2">
                  <span>🕐</span>
                  <span>Mon-Fri: 9:00 AM - 4:00 PM</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>© 2026 Bismillah Educational Trust. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="payment-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Apply for Admission</h3>
            
            <div className="mb-4">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91XXXXXXXXXX"
                className="form-input"
                data-testid="payment-phone-input"
              />
            </div>

            <div className="mb-6">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="form-input"
                data-testid="payment-email-input"
              />
            </div>

            <div className="flex gap-4">
              <button 
                onClick={handlePayment} 
                disabled={loading}
                className="primary-btn flex-1"
                data-testid="pay-button"
              >
                {loading ? 'Processing...' : 'Pay ₹200'}
              </button>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                data-testid="cancel-payment-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="login-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Parent Login</h3>
            
            <div className="mb-4">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="your@email.com"
                className="form-input"
                disabled={otpSent}
                data-testid="login-email-input"
              />
            </div>

            {otpSent && (
              <div className="mb-4">
                <label className="form-label">Enter OTP</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit OTP"
                  className="form-input"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>
            )}

            <div className="flex gap-4">
              {!otpSent ? (
                <button 
                  onClick={handleSendOTP} 
                  disabled={loading}
                  className="primary-btn flex-1"
                  data-testid="send-otp-btn"
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              ) : (
                <button 
                  onClick={handleVerifyOTP} 
                  disabled={loading}
                  className="primary-btn flex-1"
                  data-testid="verify-otp-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </button>
              )}
              <button 
                onClick={() => {
                  setShowLoginModal(false);
                  setOtpSent(false);
                  setLoginEmail('');
                  setOtp('');
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                data-testid="cancel-login-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;